import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import Icon from "../icons/Icon";
import MicroLabel from "../ui/MicroLabel";
import { trackCtaClick } from "../../utils/analytics";
import {
  EXTERNAL_LINK_PROPS,
  phoneDisplay,
  responseNote,
  telUrl,
  whatsappUrl
} from "../../utils/contact";
import styles from "./ContactDock.module.css";

/*
 * ContactDock — the persistent "talk to a human now" affordance.
 *
 * Two shapes, one component:
 *   >= 721px  a compact floating stack in the bottom-right gutter, sitting
 *             directly above ScrollToTopButton. WhatsApp is the primary
 *             action; each button reveals its label (and the response note /
 *             phone number) on hover AND on keyboard focus.
 *   <= 720px  a sticky bottom bar with two large side-by-side targets and a
 *             single reassurance line, safe-area aware.
 *
 * The language is the header's, mirrored into the opposite corner: WhatsApp is
 * THE LIME PILL (the global .btn — lime fill, ink glyph and label) and Call is
 * the ink-outlined box beside it. The box is PAPER-FILLED, not transparent:
 * olive and void sections scroll underneath this chrome, and an ink outline
 * with nothing behind it disappears on them.
 *
 * The bar is fixed, so on mobile it would otherwise sit permanently on top of
 * the last section and the footer. The component therefore also renders a
 * zero-height-on-desktop flow spacer; mounting ContactDock AFTER <Footer />
 * inside .page-shell puts that spacer at the end of the document so the page
 * grows by exactly the bar's height and everything stays reachable. No global
 * stylesheet or body padding is touched.
 *
 * Social profile links deliberately live elsewhere (FloatingSocialBar). This
 * dock is only ever the two links that start a conversation.
 */

/* TODO(content): UI labels only. The number, the pre-filled WhatsApp opener and
   the reassurance line are NOT here — they come from SITE_CONTENT.contact via
   utils/contact, so every surface quotes the same ones.
   WCAG 2.5.3: each accessible name must keep containing its visible label
   verbatim ("WhatsApp" inside whatsappName, "Call" inside callName). */
const LABELS = {
  region: "Talk to Appinfigo",
  whatsapp: "WhatsApp",
  whatsappName: "Chat on WhatsApp",
  call: "Call",
  callName: "Call Appinfigo"
};

function ContactDock() {
  const whatsappHref = whatsappUrl("default");
  const callHref = telUrl();
  const phone = phoneDisplay();
  const note = responseNote();

  /* utils/contact returns "" when a channel is not configured, and the rule is
     render nothing rather than a dead link. With neither channel there is no
     dock at all — and no spacer, so the page keeps its normal height. */
  if (!whatsappHref && !callHref) {
    return null;
  }

  return (
    <>
      <div className={styles.spacer} aria-hidden="true" />

      {/* data-surface, never data-tone: this is a paper surface of its own, but
          data-tone is the Header's signal and belongs to Sections only. */}
      <aside
        className={styles.dock}
        aria-label={LABELS.region}
        data-analytics-source="contact_dock"
        data-surface="paper"
      >
        {note ? (
          <MicroLabel as="p" className={styles.barNote}>
            {note}
          </MicroLabel>
        ) : null}

        <div className={styles.actions}>
          {whatsappHref ? (
            <a
              className={`btn ${styles.action}`}
              href={whatsappHref}
              aria-label={LABELS.whatsappName}
              data-surface="lime"
              onClick={() => trackCtaClick("WhatsApp", "contact_dock", whatsappHref)}
              {...EXTERNAL_LINK_PROPS}
            >
              {/* The site icon set has no WhatsApp glyph (it is a third-party
                  mark, not a system icon), so the mark comes from react-icons —
                  the same exception FloatingSocialBar makes. It is an imported
                  component, so no SVG is hand-written here, and it draws in
                  currentColor: ink on the lime pill, never WhatsApp's green.
                  18px, not 20: a filled mark reads heavier than a 1.5 stroke. */}
              <FaWhatsapp
                size={18}
                className={styles.glyph}
                aria-hidden="true"
                focusable="false"
              />
              {/* The label keeps the .btn voice it inherits (14px / 700 / caps),
                  on the bar and inside the desktop hover panel alike. */}
              <span className={styles.actionText}>
                <span>{LABELS.whatsapp}</span>
                {note ? (
                  <span className={`micro ${styles.actionMeta}`}>{note}</span>
                ) : null}
              </span>
            </a>
          ) : null}

          {callHref ? (
            <a
              className={`btn ${styles.action} ${styles.call}`}
              href={callHref}
              aria-label={phone ? `${LABELS.callName} on ${phone}` : LABELS.callName}
              onClick={() => trackCtaClick("Call", "contact_dock", callHref)}
            >
              <Icon name="phone" size={20} className={styles.glyph} />
              <span className={styles.actionText}>
                <span>{LABELS.call}</span>
                {phone ? (
                  <span className={`micro ${styles.actionMeta}`}>{phone}</span>
                ) : null}
              </span>
            </a>
          ) : null}
        </div>
      </aside>
    </>
  );
}

export default ContactDock;
